import { useCallback, useEffect, useRef, useState } from "react";

export default function usePaginatedResource(
  loadPage,
  { initialLimit = 25, onError } = {},
) {
  const [page, setPage] = useState({ items: [], total: 0, limit: initialLimit, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const requestId = useRef(0);
  const loaderRef = useRef(loadPage);
  const errorRef = useRef(onError);
  loaderRef.current = loadPage;
  errorRef.current = onError;

  useEffect(() => {
    const id = ++requestId.current;
    const controller = new AbortController();
    setLoading(true);
    Promise.resolve(
      loaderRef.current(
        { limit: page.limit, offset: page.offset },
        { signal: controller.signal },
      ),
    )
      .then((nextPage) => {
        if (id === requestId.current && nextPage) setPage(nextPage);
      })
      .catch((error) => {
        if (id === requestId.current && error?.name !== "CanceledError" && error?.name !== "AbortError") {
          errorRef.current?.(error);
        }
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    return () => controller.abort();
  }, [page.limit, page.offset, revision]);

  const changePage = useCallback((pagination) => {
    setPage((current) => ({ ...current, ...pagination }));
  }, []);

  const reload = useCallback(() => setRevision((value) => value + 1), []);

  const updateItems = useCallback((update) => {
    setPage((current) => ({
      ...current,
      items: typeof update === "function" ? update(current.items) : update,
    }));
  }, []);

  return { page, loading, changePage, reload, updateItems };
}
